import { AddressDetails } from '@vend/business-components/dist/components/AddressForm/types'
import isEmpty from 'lodash/isEmpty'
import { FEATURE_CAPTURE_ADDRESS_SELL_SCREEN } from '../../../../../../constants'
import {
  FulfilmentDetail,
  FulfilmentType,
} from '../../../../../../models/fulfilment'
import { getFeatureFromCache } from '../../../../../../util/feature-cache'
import { VD_COUNTRY_CODES } from '../../../../../../vend-ui/constants'
import {
  cityValidation,
  countryValidation,
  isAllAddressFieldsEmpty,
  stateValidation,
  streetAddress1Validation,
  streetAddress2Validation,
  zipcodeValidation,
} from '../../address/address-validation'

export const AddressErrors = {
  NONE: '',
  // When shipping and billing addresses are empty
  ADD_ADDRESS: 'Add shipping and billing address to complete the sale',
  // When shipping/billing address is iscorrect
  ADDRESS_INCORRECT: 'Update incorrect address details to complete the sale',
  // When shipping address is empty, billing is correct or incorrect
  ADDRESS_REQUIRED: 'Shipping & billing address required for fulfillment',
}

type AddressErrorMessages = typeof AddressErrors[keyof typeof AddressErrors]

export const AddressErrorsStatus = {
  EMPTYSHIPPING_EMPTYBILLING: 'EMPTYSHIPPING_EMPTYBILLING',
  EMPTYSHIPPING_CORRECTBILLING: 'EMPTYSHIPPING_CORRECTBILLING',
  EMPTYSHIPPING_INCORRECTBILLING: 'EMPTYSHIPPING_INCORRECTBILLING',
  INCORRECTSHIPPING_EMPTYBILLING: 'INCORRECTSHIPPING_EMPTYBILLING',
  INCORRECTSHIPPING_CORRECTBILLING: 'INCORRECTSHIPPING_CORRECTBILLING',
  INCORRECTSHIPPING_INCORRECTBILLING: 'INCORRECTSHIPPING_INCORRECTBILLING',
  CORRECTSHIPPING_EMPTYBILLING: 'CORRECTSHIPPING_EMPTYBILLING',
  CORRECTSHIPPING_INCORRECTBILLING: 'CORRECTSHIPPING_INCORRECTBILLING',
  CORRECTSHIPPING_CORRECTBILLING: 'CORRECTSHIPPING_CORRECTBILLING',
}

export type AddressErrorStatusTypes = typeof AddressErrorsStatus[keyof typeof AddressErrorsStatus]

type FulfillmentAddressDetails = {
  isSameBillingAddress: boolean
  isAddressEmpty: boolean
  formattedShippingAddress: string
  formattedBillingAddress: string
  error: AddressErrorMessages
  status: AddressErrorStatusTypes
  shouldFixSaleAddresses: boolean
  isFulfillmentAddressNeedToFix: (
    fulfilmentDetail?: FulfilmentDetail
  ) => boolean
}

const getIsSameBillingAddress = (
  fulfilmentDetail: FulfilmentDetail | undefined
): boolean => {
  if (
    isEmpty(fulfilmentDetail) ||
    isEmpty(fulfilmentDetail?.shippingAddress) ||
    isEmpty(fulfilmentDetail?.billingAddress)
  )
    return false

  return Object.entries(fulfilmentDetail!.shippingAddress!).every(
    ([key, value]) =>
      fulfilmentDetail!.billingAddress![key as keyof AddressDetails] === value
  )
}

const getFormattedAddress = (
  addressDetails: AddressDetails | undefined
): string => {
  if (isEmpty(addressDetails)) return ''

  const countryName =
    VD_COUNTRY_CODES.find(
      country => country.code === addressDetails!.country_id
    )?.name || ''
  const stateAndCountry = `${addressDetails!.state} ${countryName}`.trim()
  const formattedAddressFields: string[] = [
    addressDetails!.address_1,
    addressDetails!.address_2 || '',
    addressDetails!.city,
    addressDetails!.postcode,
    stateAndCountry,
  ].filter(field => field && !!field.trim())

  return formattedAddressFields.join(', ')
}

const getIsAddressCorrect = (
  addressDetails: AddressDetails | undefined
): boolean => {
  if (isEmpty(addressDetails)) return true

  return (
    streetAddress1Validation(addressDetails?.address_1).isValid &&
    streetAddress2Validation(addressDetails?.address_2).isValid &&
    cityValidation(addressDetails?.city).isValid &&
    zipcodeValidation(addressDetails?.postcode, addressDetails?.country_id)
      .isValid &&
    stateValidation(addressDetails?.state, addressDetails?.country_id)
      .isValid &&
    countryValidation(addressDetails?.country_id).isValid
  )
}

const getAddressError = (
  fulfilmentDetail: FulfilmentDetail | undefined
): { error: AddressErrorMessages; status: AddressErrorStatusTypes } => {
  if (isEmpty(fulfilmentDetail))
    return {
      error: AddressErrors.ADD_ADDRESS,
      status: AddressErrorsStatus.EMPTYSHIPPING_EMPTYBILLING,
    }

  if (isAllAddressFieldsEmpty(fulfilmentDetail?.shippingAddress)) {
    if (isAllAddressFieldsEmpty(fulfilmentDetail?.billingAddress))
      return {
        error: AddressErrors.ADD_ADDRESS,
        status: AddressErrorsStatus.EMPTYSHIPPING_EMPTYBILLING,
      }
    if (getIsAddressCorrect(fulfilmentDetail?.billingAddress))
      return {
        error: AddressErrors.ADDRESS_REQUIRED,
        status: AddressErrorsStatus.EMPTYSHIPPING_CORRECTBILLING,
      }
    return {
      error: AddressErrors.ADDRESS_REQUIRED,
      status: AddressErrorsStatus.EMPTYSHIPPING_INCORRECTBILLING,
    }
  }

  if (!getIsAddressCorrect(fulfilmentDetail?.shippingAddress)) {
    if (isAllAddressFieldsEmpty(fulfilmentDetail?.billingAddress))
      return {
        error: AddressErrors.ADDRESS_REQUIRED,
        status: AddressErrorsStatus.INCORRECTSHIPPING_EMPTYBILLING,
      }
    if (getIsAddressCorrect(fulfilmentDetail?.billingAddress))
      return {
        error: AddressErrors.ADDRESS_INCORRECT,
        status: AddressErrorsStatus.INCORRECTSHIPPING_CORRECTBILLING,
      }
    return {
      error: AddressErrors.ADDRESS_INCORRECT,
      status: AddressErrorsStatus.INCORRECTSHIPPING_INCORRECTBILLING,
    }
  }

  if (isAllAddressFieldsEmpty(fulfilmentDetail?.billingAddress))
    return {
      error: AddressErrors.ADDRESS_REQUIRED,
      status: AddressErrorsStatus.CORRECTSHIPPING_EMPTYBILLING,
    }
  if (!getIsAddressCorrect(fulfilmentDetail?.billingAddress))
    return {
      error: AddressErrors.ADDRESS_INCORRECT,
      status: AddressErrorsStatus.CORRECTSHIPPING_INCORRECTBILLING,
    }
  return {
    error: AddressErrors.NONE,
    status: AddressErrorsStatus.CORRECTSHIPPING_CORRECTBILLING,
  }
}

const isFulfillmentAddressNeedToFix = (fulfilmentDetail?: FulfilmentDetail) => {
  const addressErrorDetails = getAddressError(fulfilmentDetail)

  return (
    fulfilmentDetail?.type === FulfilmentType.DELIVERY &&
    (addressErrorDetails.status ===
      AddressErrorsStatus.CORRECTSHIPPING_INCORRECTBILLING ||
      addressErrorDetails.status ===
        AddressErrorsStatus.EMPTYSHIPPING_INCORRECTBILLING ||
      addressErrorDetails.status ===
        AddressErrorsStatus.EMPTYSHIPPING_CORRECTBILLING ||
      addressErrorDetails.status ===
        AddressErrorsStatus.INCORRECTSHIPPING_INCORRECTBILLING ||
      addressErrorDetails.status ===
        AddressErrorsStatus.INCORRECTSHIPPING_CORRECTBILLING)
  )
}

export const useLineItemFulfillmentAddressDetails = (
  fulfilmentDetail: FulfilmentDetail | undefined
): FulfillmentAddressDetails => {
  if (!getFeatureFromCache(FEATURE_CAPTURE_ADDRESS_SELL_SCREEN)) {
    return {
      isSameBillingAddress: true,
      isAddressEmpty: true,
      formattedShippingAddress: '',
      formattedBillingAddress: '',
      error: AddressErrors.NONE,
      status: AddressErrorsStatus.CORRECTSHIPPING_CORRECTBILLING,
      shouldFixSaleAddresses: false,
      isFulfillmentAddressNeedToFix: () => false,
    }
  }

  const addressErrorDetails = getAddressError(fulfilmentDetail)

  const shouldFixSaleAddresses =
    addressErrorDetails.status ===
      AddressErrorsStatus.CORRECTSHIPPING_INCORRECTBILLING ||
    addressErrorDetails.status ===
      AddressErrorsStatus.EMPTYSHIPPING_INCORRECTBILLING ||
    addressErrorDetails.status ===
      AddressErrorsStatus.EMPTYSHIPPING_CORRECTBILLING ||
    addressErrorDetails.status ===
      AddressErrorsStatus.INCORRECTSHIPPING_INCORRECTBILLING ||
    addressErrorDetails.status ===
      AddressErrorsStatus.INCORRECTSHIPPING_CORRECTBILLING

  const fulfillmentAddressDetails: FulfillmentAddressDetails = {
    isSameBillingAddress: getIsSameBillingAddress(fulfilmentDetail),
    isAddressEmpty: isAllAddressFieldsEmpty(fulfilmentDetail?.shippingAddress),
    formattedShippingAddress: getFormattedAddress(
      fulfilmentDetail?.shippingAddress
    ),
    formattedBillingAddress: getFormattedAddress(
      fulfilmentDetail?.billingAddress
    ),
    ...addressErrorDetails,
    shouldFixSaleAddresses,
    isFulfillmentAddressNeedToFix,
  }

  return fulfillmentAddressDetails
}
